import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OpSelect,
  OpSelectSingleProps,
} from 'new-components/DLS/OpSelect/OpSelect';
import { sortOptionsAlphabetically } from 'utils/sortOptionsAlphabetically';
import { useSupportedLanguages } from 'utils/customHooks/useSupportedLanguages';

type StandaloneLanguageSelectorProps = Pick<
  OpSelectSingleProps<string, { value: string; label: string }>,
  'value' | 'onChange'
> & {
  labelMode?: 'name' | 'nativeName';
  normalizeLanguageCode?: boolean;
  defaultValue?: string;
};

export const LanguageSelector = ({
  labelMode = 'name',
  normalizeLanguageCode = false,
  defaultValue,
  value,
  onChange,
}: StandaloneLanguageSelectorProps) => {
  const { t } = useTranslation();
  const supportedLanguages = useSupportedLanguages();

  const languageOptions = useMemo(
    () =>
      supportedLanguages.map(({ code, name, nativeName }) => ({
        value: code,
        label: labelMode === 'nativeName' ? nativeName : name,
      })),
    [labelMode, supportedLanguages],
  );

  const normalizedValue =
    normalizeLanguageCode && typeof value === 'string'
      ? value.split('-')[0]
      : value;

  return (
    <OpSelect
      value={normalizedValue ?? defaultValue}
      onChange={onChange}
      placeholder={t('Select language')}
      options={languageOptions}
      filterSort={sortOptionsAlphabetically}
    />
  );
};
